import React, { useState, useEffect } from 'react';
import { tradeLicenseApi } from '../../../services/tradeLicenseApi';
import BilingualInput from '../../../components/TradeLicense/BilingualInput';
import CertificateSettings from './CertificateSettings';
import './Settings.css';

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Constants                                                                   */
/* ─────────────────────────────────────────────────────────────────────────── */
const BUSINESS_TYPES = [
  { value: 'retail', label: 'Retail' },
  { value: 'wholesale', label: 'Wholesale' },
  { value: 'service', label: 'Service' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'restaurant', label: 'Restaurant / Hotel' },
  { value: 'medical', label: 'Medical / Pharmacy' },
  { value: 'education', label: 'Education' },
  { value: 'entertainment', label: 'Entertainment' },
  { value: 'other', label: 'Other' },
];

const EMPTY_CATEGORY = {
  code: '',
  name_en: '',
  name_hi: '',
  description_en: '',
  description_hi: '',
  business_type: 'retail',
  application_fee: '',
  annual_renewal_fee: '',
  late_fee_per_day: 0,
  max_validity_days: 365,
  inspection_required: true,
  approval_required: true,
  is_active: true,
};

const ROLE_OPTIONS = [
  { value: '', label: 'No specific role' },
  { value: 'license_clerk', label: 'License Clerk' },
  { value: 'verifier', label: 'Verifier' },
  { value: 'inspector', label: 'Inspector' },
  { value: 'approver', label: 'Approver' },
  { value: 'account_officer', label: 'Account Officer' },
  { value: 'municipality_admin', label: 'Municipality Admin' },
  { value: 'report_viewer', label: 'Report Viewer' },
];

const STAGE_TYPE_OPTIONS = [
  { value: 'verification', label: 'Verification' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'approval', label: 'Approval' },
  { value: 'payment', label: 'Payment Pending' },
  { value: 'issued', label: 'Issued' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'clarification', label: 'Clarification Required' },
  { value: 'submitted', label: 'Submitted' },
];

const EMPTY_WORKFLOW = {
  code: '',
  name_en: '',
  name_hi: '',
  stage_type: 'approval',
  order: 10,
  is_active: true,
  is_terminal_state: false,
  auto_notify_applicant: true,
  notify_by_email: true,
  notify_by_sms: false,
  requires_officer_action: true,
  assigned_role: 'approver',
  assigned_user: '',
  notification_template: '',
};

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Main Component                                                              */
/* ─────────────────────────────────────────────────────────────────────────── */
const Settings = () => {
  const [categories, setCategories]     = useState([]);
  const [workflows, setWorkflows]       = useState([]);
  const [officers, setOfficers]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [activeTab, setActiveTab]       = useState('categories');

  /* Category state */
  const [showCatForm, setShowCatForm]   = useState(false);  // add-new form
  const [editingCatId, setEditingCatId] = useState(null);
  const [catForm, setCatForm]           = useState(EMPTY_CATEGORY);
  const [catSaving, setCatSaving]       = useState(false);
  const [catError, setCatError]         = useState('');

  /* Workflow state */
  const [showWfForm, setShowWfForm]     = useState(false);
  const [editingWfId, setEditingWfId]   = useState(null);
  const [wfForm, setWfForm]             = useState({});
  const [wfSaving, setWfSaving]         = useState(false);

  /* ── data fetching ────────────────────────────────────────────────────── */
  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [catRes, wfRes, officersRes] = await Promise.all([
        tradeLicenseApi.getCategories(),
        tradeLicenseApi.getWorkflowStages(),
        tradeLicenseApi.getAssignableOfficers(),
      ]);
      setCategories(catRes.data.results || catRes.data);
      setWorkflows(wfRes.data.results  || wfRes.data);
      setOfficers(officersRes.data.results || officersRes.data || []);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    } finally {
      setLoading(false);
    }
  };

  /* ── Category helpers ─────────────────────────────────────────────────── */
  const openAddCat = () => {
    setEditingCatId(null);
    setCatForm(EMPTY_CATEGORY);
    setCatError('');
    setShowCatForm(true);
  };

  const openEditCat = (cat) => {
    setShowCatForm(false);
    setEditingCatId(cat.id);
    setCatForm({ ...cat });
    setCatError('');
  };

  const cancelCat = () => {
    setShowCatForm(false);
    setEditingCatId(null);
    setCatError('');
  };

  const handleCatInput = (e) => {
    const { name, value, type, checked } = e.target;
    setCatForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const saveCategory = async () => {
    if (!catForm.code || !catForm.name_en || !catForm.business_type ||
        catForm.application_fee === '' || catForm.annual_renewal_fee === '') {
      setCatError('Code, English name, business type, application fee, and renewal fee are required.');
      return;
    }
    setCatSaving(true);
    setCatError('');
    try {
      if (editingCatId) {
        await tradeLicenseApi.updateCategory(editingCatId, catForm);
      } else {
        await tradeLicenseApi.createCategory(catForm);
      }
      cancelCat();
      fetchAll();
    } catch (err) {
      const msg = err.response?.data
        ? Object.values(err.response.data).flat().join(' ')
        : err.message;
      setCatError(msg);
    } finally {
      setCatSaving(false);
    }
  };

  const deleteCategory = async (id, name) => {
    if (!window.confirm(`Deactivate category "${name}"? It will no longer appear in forms.`)) return;
    try {
      await tradeLicenseApi.deleteCategory(id);
      fetchAll();
    } catch (err) {
      alert('Error deleting category: ' + err.message);
    }
  };

  /* ── Workflow helpers ─────────────────────────────────────────────────── */
  const openAddWf = () => {
    setEditingWfId(null);
    setWfForm({
      ...EMPTY_WORKFLOW,
      order: workflows.length ? Math.max(...workflows.map(wf => Number(wf.order) || 0)) + 1 : 1,
    });
    setShowWfForm(true);
  };
  const openEditWf = (wf) => {
    setShowWfForm(false);
    setEditingWfId(wf.id);
    setWfForm({ ...wf, assigned_user: wf.assigned_user || '' });
  };
  const cancelWf   = () => { setEditingWfId(null); setShowWfForm(false); };

  const handleWfInput = (e) => {
    const { name, value, type, checked } = e.target;
    setWfForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const saveWorkflow = async () => {
    setWfSaving(true);
    try {
      const payload = { ...wfForm, assigned_user: wfForm.assigned_user || null };
      if (editingWfId) {
        await tradeLicenseApi.updateWorkflowStage(editingWfId, payload);
      } else {
        await tradeLicenseApi.createWorkflowStage(payload);
      }
      cancelWf();
      fetchAll();
    } catch (err) {
      alert('Error saving workflow: ' + err.message);
    } finally {
      setWfSaving(false);
    }
  };

  const deleteWorkflow = async (wf) => {
    if (!window.confirm(`Deactivate workflow stage "${wf.name_en}"?`)) return;
    try {
      await tradeLicenseApi.deleteWorkflowStage(wf.id);
      fetchAll();
    } catch (err) {
      alert('Error deactivating workflow stage: ' + err.message);
    }
  };

  /* ── Render ──────────────────────────────────────────────────────────── */
  return (
    <div className="settings-container">
      <h1 className="page-title">⚙️ Trade License Configuration</h1>

      {/* ── Tabs ─────────────────────────────────────────────────────────── */}
      <div className="settings-tabs">
        <button
          className={`tab-btn ${activeTab === 'categories' ? 'active' : ''}`}
          onClick={() => { setActiveTab('categories'); cancelCat(); }}
        >
          🏷️ Trade Categories
        </button>
        <button
          className={`tab-btn ${activeTab === 'workflows' ? 'active' : ''}`}
          onClick={() => { setActiveTab('workflows'); cancelCat(); }}
        >
          🔄 Workflow Stages
        </button>
        <button
          className={`tab-btn ${activeTab === 'certificate' ? 'active' : ''}`}
          onClick={() => { setActiveTab('certificate'); cancelCat(); }}
        >
          📜 Certificate
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading settings…</div>
      ) : (
        <div className="settings-content">

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/*  TRADE CATEGORIES TAB                                           */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          {activeTab === 'categories' && (
            <>
              <div className="settings-toolbar">
                <p className="settings-hint">
                  {categories.length} categor{categories.length !== 1 ? 'ies' : 'y'} configured
                </p>
                <button className="btn btn-primary" onClick={openAddCat}>
                  ➕ Add New Category
                </button>
              </div>

              {/* ── Add-new inline form ─────────────────────────────────── */}
              {showCatForm && (
                <div className="settings-item form-card">
                  <h3>🆕 New Trade Category</h3>
                  {catError && <div className="form-error">{catError}</div>}
                  <CategoryForm
                    form={catForm}
                    onInput={handleCatInput}
                    onSave={saveCategory}
                    onCancel={cancelCat}
                    saving={catSaving}
                    isNew
                  />
                </div>
              )}

              {/* ── Category list ───────────────────────────────────────── */}
              <div className="settings-list">
                {categories.length === 0 && !showCatForm ? (
                  <p className="empty-msg">No categories configured yet. Click "Add New Category" to create one.</p>
                ) : (
                  categories.map(cat => (
                    <div key={cat.id} className={`settings-item ${!cat.is_active ? 'inactive' : ''}`}>
                      {editingCatId === cat.id ? (
                        <>
                          {catError && <div className="form-error">{catError}</div>}
                          <CategoryForm
                            form={catForm}
                            onInput={handleCatInput}
                            onSave={saveCategory}
                            onCancel={cancelCat}
                            saving={catSaving}
                          />
                        </>
                      ) : (
                        <div className="item-display">
                          <div className="item-header">
                            <div>
                              <h3>
                                {cat.name_en}
                                {!cat.is_active && (
                                  <span className="badge-inactive">Inactive</span>
                                )}
                              </h3>
                              <p className="item-hindi">{cat.name_hi}</p>
                            </div>
                            <div className="item-actions">
                              <button className="btn btn-primary btn-sm" onClick={() => openEditCat(cat)}>
                                ✏️ Edit
                              </button>
                              {cat.is_active && (
                                <button
                                  className="btn btn-danger btn-sm"
                                  onClick={() => deleteCategory(cat.id, cat.name_en)}
                                >
                                  🗑️ Deactivate
                                </button>
                              )}
                            </div>
                          </div>
                          <dl className="item-details">
                            <dt>Code:</dt>           <dd><code>{cat.code}</code></dd>
                            <dt>Business Type:</dt>  <dd>{cat.business_type}</dd>
                            <dt>Application Fee:</dt><dd>₹{cat.application_fee}</dd>
                            <dt>Renewal Fee:</dt>    <dd>₹{cat.annual_renewal_fee}</dd>
                            <dt>Late Fee/Day:</dt>   <dd>₹{cat.late_fee_per_day}</dd>
                            <dt>Validity:</dt>       <dd>{cat.max_validity_days} days</dd>
                            <dt>Inspection Req:</dt> <dd>{cat.inspection_required ? '✅ Yes' : '❌ No'}</dd>
                            <dt>Approval Req:</dt>   <dd>{cat.approval_required ? '✅ Yes' : '❌ No'}</dd>
                          </dl>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </>
          )}

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/*  WORKFLOW STAGES TAB                                            */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          {activeTab === 'workflows' && (
            <div className="settings-list">
              <div className="settings-toolbar">
                <p className="settings-hint">
                  {workflows.length} workflow stage{workflows.length !== 1 ? 's' : ''} — ordered by step number
                </p>
                <button className="btn btn-primary" onClick={openAddWf}>
                  ➕ Add Workflow Level
                </button>
              </div>
              {showWfForm && (
                <div className="settings-item form-card">
                  <h3 className="form-card-title">New Workflow Level</h3>
                  <WorkflowForm
                    form={wfForm}
                    officers={officers}
                    onInput={handleWfInput}
                    onSave={saveWorkflow}
                    onCancel={cancelWf}
                    saving={wfSaving}
                    isNew
                  />
                </div>
              )}
              {workflows.length === 0 ? (
                !showWfForm && <p className="empty-msg">No workflow stages configured.</p>
              ) : (
                workflows.map(wf => (
                  <div key={wf.id} className={`settings-item ${!wf.is_active ? 'inactive' : ''}`}>
                    {editingWfId === wf.id ? (
                      <WorkflowForm
                        form={wfForm}
                        officers={officers}
                        onInput={handleWfInput}
                        onSave={saveWorkflow}
                        onCancel={cancelWf}
                        saving={wfSaving}
                      />
                    ) : (
                      <div className="item-display">
                        <div className="item-header">
                          <div>
                            <h3>
                              <span className="wf-order">#{wf.order}</span>
                              {' '}{wf.name_en}
                              {wf.is_terminal_state && <span className="badge-terminal">Terminal</span>}
                              {!wf.is_active && <span className="badge-inactive">Inactive</span>}
                            </h3>
                            <p className="item-hindi">{wf.name_hi}</p>
                          </div>
                          <div className="item-actions">
                            <button className="btn btn-primary btn-sm" onClick={() => openEditWf(wf)}>
                              ✏️ Edit
                            </button>
                            {wf.is_active && (
                              <button className="btn btn-danger btn-sm" onClick={() => deleteWorkflow(wf)}>
                                🗑️ Deactivate
                              </button>
                            )}
                          </div>
                        </div>
                        <dl className="item-details">
                          <dt>Code:</dt>          <dd><code>{wf.code}</code></dd>
                          <dt>Stage Type:</dt>    <dd>{wf.stage_type}</dd>
                          <dt>Assigned Role:</dt> <dd>{wf.assigned_role || 'Any assigned user'}</dd>
                          <dt>Assigned User:</dt> <dd>{wf.assigned_user_name || wf.assigned_user_username || 'Auto by role'}</dd>
                          <dt>Auto Notify:</dt>   <dd>{wf.auto_notify_applicant ? '✅ Yes' : '❌ No'}</dd>
                          <dt>Email:</dt>         <dd>{wf.notify_by_email ? '✅ Enabled' : '❌ Disabled'}</dd>
                          <dt>SMS:</dt>           <dd>{wf.notify_by_sms ? '✅ Enabled' : '❌ Disabled'}</dd>
                          <dt>Officer Action:</dt><dd>{wf.requires_officer_action ? '✅ Yes' : '❌ No'}</dd>
                        </dl>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/*  CERTIFICATE TAB                                                */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          {activeTab === 'certificate' && <CertificateSettings />}

        </div>
      )}
    </div>
  );
};

const WorkflowForm = ({ form, officers, onInput, onSave, onCancel, saving, isNew }) => (
  <div className="edit-form">
    <div className="form-row-3">
      <div className="form-group">
        <label>Code <span className="req">*</span></label>
        <input
          type="text"
          name="code"
          placeholder="approval_level_1"
          value={form.code || ''}
          onChange={onInput}
          disabled={!isNew}
        />
      </div>
      <div className="form-group">
        <label>Stage Type</label>
        <select name="stage_type" value={form.stage_type || 'approval'} onChange={onInput}>
          {STAGE_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label>Order</label>
        <input
          type="number"
          name="order"
          min="1"
          value={form.order ?? ''}
          onChange={onInput}
        />
      </div>
    </div>

    <BilingualInput
      label="Name"
      enName="name_en"
      hiName="name_hi"
      enValue={form.name_en || ''}
      hiValue={form.name_hi || ''}
      onChange={onInput}
      required
      fieldContext="workflow_stage"
    />

    <div className="form-row-2">
      <div className="form-group">
        <label>Assigned Role</label>
        <select name="assigned_role" value={form.assigned_role || ''} onChange={onInput}>
          {ROLE_OPTIONS.map((opt) => (
            <option key={opt.value || 'none'} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <small>Used when no fixed assigned user is selected.</small>
      </div>
      <div className="form-group">
        <label>Assigned User</label>
        <select name="assigned_user" value={form.assigned_user || ''} onChange={onInput}>
          <option value="">Auto assign by role</option>
          {officers.map((officer) => (
            <option key={officer.user} value={officer.user}>
              {officer.user_name || officer.user_username || officer.user_email} ({officer.role.replace(/_/g, ' ')})
            </option>
          ))}
        </select>
      </div>
    </div>

    <div className="form-group">
      <label>Notification Template</label>
      <input
        type="text"
        name="notification_template"
        value={form.notification_template || ''}
        onChange={onInput}
        placeholder="Optional template code"
      />
    </div>

    <div className="workflow-check-grid">
      <label><input type="checkbox" name="is_active" checked={!!form.is_active} onChange={onInput} /> Active</label>
      <label><input type="checkbox" name="requires_officer_action" checked={!!form.requires_officer_action} onChange={onInput} /> Requires officer approval</label>
      <label><input type="checkbox" name="is_terminal_state" checked={!!form.is_terminal_state} onChange={onInput} /> Terminal state</label>
      <label><input type="checkbox" name="auto_notify_applicant" checked={!!form.auto_notify_applicant} onChange={onInput} /> Notify applicant on entry</label>
      <label><input type="checkbox" name="notify_by_email" checked={!!form.notify_by_email} onChange={onInput} /> Email enabled</label>
      <label><input type="checkbox" name="notify_by_sms" checked={!!form.notify_by_sms} onChange={onInput} /> SMS enabled</label>
    </div>

    <div className="form-actions">
      <button className="btn btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
      <button className="btn btn-primary" onClick={onSave} disabled={saving || !form.code || !form.name_en}>
        {saving ? 'Saving…' : (isNew ? '✅ Create Workflow Level' : '💾 Save Changes')}
      </button>
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────────────── */
/*  CategoryForm — shared by Add + Edit                                         */
/* ─────────────────────────────────────────────────────────────────────────── */
const CategoryForm = ({ form, onInput, onSave, onCancel, saving, isNew }) => (
  <div className="edit-form">
    <div className="form-row-2">
      <div className="form-group">
        <label>Code <span className="req">*</span></label>
        <input
          type="text"
          name="code"
          placeholder="e.g. RETAIL_01"
          value={form.code || ''}
          onChange={onInput}
          disabled={!isNew}  /* code is immutable after creation */
        />
        {isNew && <small>Unique identifier — cannot be changed later</small>}
      </div>
      <div className="form-group">
        <label>Business Type <span className="req">*</span></label>
        <select name="business_type" value={form.business_type || 'retail'} onChange={onInput}>
          {BUSINESS_TYPES.map(b => (
            <option key={b.value} value={b.value}>{b.label}</option>
          ))}
        </select>
      </div>
    </div>

    <BilingualInput
      label="Name"
      enName="name_en"
      hiName="name_hi"
      enValue={form.name_en || ''}
      hiValue={form.name_hi || ''}
      onChange={onInput}
      required
      placeholderEn="e.g. Retail Shop"
      placeholderHi="e.g. खुदरा दुकान"
      fieldContext="category"
    />

    <BilingualInput
      label="Description"
      enName="description_en"
      hiName="description_hi"
      enValue={form.description_en || ''}
      hiValue={form.description_hi || ''}
      onChange={onInput}
      multiline
      rows={2}
      fieldContext="category"
    />

    <div className="form-row-3">
      <div className="form-group">
        <label>Application Fee (₹) <span className="req">*</span></label>
        <input
          type="number"
          name="application_fee"
          min="0"
          step="0.01"
          placeholder="0.00"
          value={form.application_fee ?? ''}
          onChange={onInput}
        />
      </div>
      <div className="form-group">
        <label>Annual Renewal Fee (₹) <span className="req">*</span></label>
        <input
          type="number"
          name="annual_renewal_fee"
          min="0"
          step="0.01"
          placeholder="0.00"
          value={form.annual_renewal_fee ?? ''}
          onChange={onInput}
        />
      </div>
      <div className="form-group">
        <label>Late Fee / Day (₹)</label>
        <input
          type="number"
          name="late_fee_per_day"
          min="0"
          step="0.01"
          placeholder="0.00"
          value={form.late_fee_per_day ?? 0}
          onChange={onInput}
        />
      </div>
    </div>

    <div className="form-row-2">
      <div className="form-group">
        <label>Max Validity (days)</label>
        <input
          type="number"
          name="max_validity_days"
          min="1"
          value={form.max_validity_days ?? 365}
          onChange={onInput}
        />
      </div>
      <div className="form-group form-group-checks">
        <label>
          <input
            type="checkbox"
            name="inspection_required"
            checked={!!form.inspection_required}
            onChange={onInput}
          />
          {' '}Inspection Required
        </label>
        <label>
          <input
            type="checkbox"
            name="approval_required"
            checked={!!form.approval_required}
            onChange={onInput}
          />
          {' '}Approval Required
        </label>
        <label>
          <input
            type="checkbox"
            name="is_active"
            checked={!!form.is_active}
            onChange={onInput}
          />
          {' '}Active
        </label>
      </div>
    </div>

    <div className="form-actions">
      <button className="btn btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
      <button className="btn btn-primary" onClick={onSave} disabled={saving}>
        {saving ? 'Saving…' : (isNew ? '✅ Create Category' : '💾 Save Changes')}
      </button>
    </div>
  </div>
);

export default Settings;